import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { NavBar } from '../components/NavBar';
import { BackToHome } from '../components/BackToHome';
import { useI18n } from '../i18n/I18nContext';
import { useLocalizedContent } from '../i18n/useLocalizedContent';
import { HandHeart, Plane, FileText, ExternalLink, Heart, ArrowRight } from 'lucide-react';
import { Tx } from '../components/EditMode';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const extractYouTubeId = (url) => {
  if (!url) return '';
  if (/^[a-zA-Z0-9_-]{11}$/.test(url)) return url;
  const m = url.match(/(?:youtube\.com\/(?:.*v=|embed\/|shorts\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : '';
};

const InfoButton = ({ icon: Icon, color, title, subtitle, href, testid }) => (
  <a data-testid={testid} href={href} target="_blank" rel="noopener noreferrer"
     className="group relative w-full flex items-center gap-4 p-5 sm:p-6 rounded-2xl bg-white shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl overflow-hidden">
    <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${color.accent}`}></span>
    <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center flex-shrink-0 ${color.iconBg} shadow-sm`}>
      <Icon className={`w-7 h-7 sm:w-8 sm:h-8 ${color.iconText}`} />
    </div>
    <div className="flex-1 min-w-0 text-left">
      <h4 className="font-bold text-base sm:text-lg text-slate-900 font-['Outfit'] tracking-tight">{title}</h4>
      {subtitle && <p className="text-xs sm:text-sm text-slate-600 mt-0.5 leading-relaxed">{subtitle}</p>}
    </div>
    <div className={`flex items-center justify-center w-9 h-9 rounded-full ${color.iconBg} ${color.iconText} group-hover:scale-110 transition-transform flex-shrink-0`}>
      <ExternalLink className="w-4 h-4" />
    </div>
  </a>
);

export const MissionPage = () => {
  const [settings, setSettings] = useState(null);
  const { t, lang } = useI18n();
  const localized = useLocalizedContent();

  useEffect(() => {
    axios.get(`${API}/settings/public`)
      .then(r => setSettings(r.data))
      .catch((e) => console.warn('settings load failed', e?.message));
  }, []);

  // Hero background video (sits BEHIND the header). Defaults to U-qvHmDbzyM if unset.
  const heroVideoSource = settings?.mission_hero_video_url ?? 'U-qvHmDbzyM';
  const heroBgId = extractYouTubeId(heroVideoSource) || (heroVideoSource && /^[a-zA-Z0-9_-]{11}$/.test(heroVideoSource) ? heroVideoSource : '');

  // Existing "Watch our story" video (rendered below the link buttons).
  const ytUrl = lang === 'no' && settings?.youtube_url_no ? settings.youtube_url_no : settings?.youtube_url;
  const ytId = extractYouTubeId(ytUrl);

  const wycliffeUrl = lang === 'no' && settings?.wycliffe_info_url_no
    ? settings.wycliffe_info_url_no
    : settings?.wycliffe_info_url;
  const jaarsUrl = lang === 'no' && settings?.jaars_info_url_no
    ? settings.jaars_info_url_no
    : settings?.jaars_info_url;
  const canvaUrl = lang === 'no' && settings?.canva_newsletter_url_no
    ? settings.canva_newsletter_url_no
    : settings?.canva_newsletter_url;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50 to-amber-50">
      <NavBar />
      <BackToHome />

      {/* ===== Cinematic Hero with background YouTube video ===== */}
      <div data-testid="mission-hero" className="relative w-full overflow-hidden bg-slate-900">
        {/* Background YouTube video — autoplay muted loop, sized to cover.
            Sits at z-0 with reduced opacity so YouTube error/branding never competes
            with the foreground content. If the video fails to embed (owner disabled
            embedding), users see a dark navy hero instead. */}
        {heroBgId ? (
          <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-70 blur-sm">
            <iframe
              data-testid="mission-hero-bg-video"
              title="Mission background video"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                         w-[177.78vh] min-w-full h-[56.25vw] min-h-full scale-110"
              src={`https://www.youtube.com/embed/${heroBgId}?autoplay=1&mute=1&loop=1&playlist=${heroBgId}&controls=0&modestbranding=1&showinfo=0&rel=0&iv_load_policy=3&playsinline=1&disablekb=1`}
              frameBorder="0"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-sky-900 via-blue-900 to-slate-900" />
        )}

        {/* Heavy gradient overlay → keeps the foreground content readable even if
            the background video shows YouTube's "Video unavailable" placeholder. */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/65 to-slate-900/90 z-[1]"></div>

        {/* Hero content */}
        <div className="relative z-[2] max-w-3xl mx-auto px-4 py-20 sm:py-28 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full bg-amber-300/90 text-amber-900 text-xs font-bold uppercase tracking-wider backdrop-blur-sm">
            <Plane className="w-3.5 h-3.5" />
            <Tx k="mission.eyebrow" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 font-['Outfit'] tracking-tight drop-shadow-2xl">
            <Tx k="mission.heading" />
          </h1>
          {(localized(settings, 'mission_intro') || t('mission.intro_default')) && (
            <p className="text-base sm:text-lg text-white/90 font-['Manrope'] leading-relaxed max-w-2xl mx-auto whitespace-pre-wrap drop-shadow">
              {localized(settings, 'mission_intro') || t('mission.intro_default')}
            </p>
          )}
        </div>
      </div>

      {/* ===== Body ===== */}
      <div className="max-w-3xl mx-auto px-4 py-10 sm:py-14">
        {/* YouTube "watch our story" video */}
        {ytId && (
          <div data-testid="mission-youtube" className="mb-8 rounded-2xl overflow-hidden border-2 border-red-100 shadow-lg">
            <div className="aspect-video bg-black">
              <iframe
                width="100%" height="100%"
                src={`https://www.youtube.com/embed/${ytId}`}
                title={localized(settings, 'youtube_title') || 'Watch our story'}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        )}

        {/* Link buttons */}
        <div className="space-y-3">
          {wycliffeUrl && (
            <InfoButton
              testid="mission-link-wycliffe"
              icon={HandHeart}
              color={{ accent: 'bg-emerald-500', iconBg: 'bg-emerald-100', iconText: 'text-emerald-600' }}
              title={t('mission.wycliffe.title')}
              subtitle={t('mission.wycliffe.subtitle')}
              href={wycliffeUrl}
            />
          )}
          {jaarsUrl && (
            <InfoButton
              testid="mission-link-jaars"
              icon={Plane}
              color={{ accent: 'bg-amber-500', iconBg: 'bg-amber-100', iconText: 'text-amber-600' }}
              title={t('mission.jaars.title')}
              subtitle={t('mission.jaars.subtitle')}
              href={jaarsUrl}
            />
          )}
          {canvaUrl && (
            <InfoButton
              testid="mission-link-newsletter"
              icon={FileText}
              color={{ accent: 'bg-purple-500', iconBg: 'bg-purple-100', iconText: 'text-purple-600' }}
              title={t('mission.newsletter.title')}
              subtitle={t('mission.newsletter.subtitle')}
              href={canvaUrl}
            />
          )}
        </div>

        {/* CTA back to monthly partner */}
        <div className="mt-12 text-center bg-white rounded-2xl shadow-md border border-slate-100 p-8">
          <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Outfit'] tracking-tight mb-3">
            <Tx k="mission.cta_heading" />
          </h3>
          <p className="text-sm sm:text-base text-slate-600 mb-6 max-w-xl mx-auto leading-relaxed">
            <Tx k="mission.cta_body" />
          </p>
          <a
            data-testid="mission-partner-cta"
            href="/signup"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-amber-500 text-slate-900 rounded-full font-bold hover:bg-amber-400 shadow-lg hover:shadow-amber-500/40 hover:-translate-y-0.5 transition-all"
          >
            <Heart className="w-4 h-4" />
            <Tx k="home.cta.partner_monthly" />
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
};
